// AI Service for intelligent CRM features
import { Contact, Activity, AIInsight, ContactSummary, ResponseSuggestion, DuplicateContact, Lead } from '../types';
import { openaiService } from './openaiService';

class AIService {
  // Simulate AI processing delay
  private async simulateAIProcessing(delay: number = 1000): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  // Generate automatic contact summary
  async generateContactSummary(contact: Contact, activities: Activity[]): Promise<ContactSummary> {
    await this.simulateAIProcessing(1500);

    // Try to use OpenAI if configured
    if (openaiService.isConfigured()) {
      try {
        console.log('🤖 Generando resumen con OpenAI...');
        const aiSummary = await openaiService.generateContactSummary(contact.fullName, contact, activities);
        
        return {
          contactId: contact.id,
          summary: aiSummary,
          keyPoints: this.extractKeyPoints(contact, activities),
          nextSteps: this.suggestNextSteps(contact, activities),
          riskLevel: this.assessRiskLevel(contact, activities),
          lastUpdated: new Date()
        };
      } catch (error) {
        console.error('Error using OpenAI, falling back to mock:', error);
      }
    }

    const recentActivities = activities
      .filter(a => a.contactId === contact.id)
      .sort((a, b) => b.datetime.getTime() - a.datetime.getTime())
      .slice(0, 10);

    // Simulate AI analysis
    const keyPoints = this.extractKeyPoints(contact, recentActivities);
    const summary = this.generateSummaryText(contact, recentActivities);
    const nextSteps = this.suggestNextSteps(contact, recentActivities);
    const riskLevel = this.assessRiskLevel(contact, recentActivities);

    return {
      contactId: contact.id,
      summary,
      keyPoints,
      nextSteps,
      riskLevel,
      lastUpdated: new Date()
    };
  }

  // Generate response suggestions
  async generateResponseSuggestion(context: string, contactInfo: Contact): Promise<ResponseSuggestion> {
    await this.simulateAIProcessing(800);

    // Try to use OpenAI if configured
    if (openaiService.isConfigured()) {
      try {
        console.log('🤖 Generando respuesta con OpenAI...');
        const aiResponse = await openaiService.generateResponseSuggestion(context, contactInfo);
        
        return {
          id: Date.now().toString(),
          context,
          suggestedResponse: aiResponse,
          tone: 'professional',
          confidence: 90
        };
      } catch (error) {
        console.error('Error using OpenAI, falling back to mock:', error);
      }
    }

    const suggestions = [
      {
        context: 'initial_inquiry',
        response: `Hola ${contactInfo.fullName},\n\nGracias por contactar con Cero Ideas. Hemos recibido tu consulta sobre ${contactInfo.productInterest} y nos complace poder ayudarte.\n\nNos gustaría programar una reunión para entender mejor tus necesidades y presentarte nuestras soluciones. ¿Qué tal si hablamos esta semana?\n\nQuedo a la espera de tu respuesta.\n\nSaludos,\nEquipo Cero Ideas`,
        tone: 'professional' as const
      },
      {
        context: 'follow_up',
        response: `Hola ${contactInfo.fullName},\n\nEspero que estés bien. Quería hacer un seguimiento de nuestra conversación anterior sobre ${contactInfo.productInterest}.\n\n¿Has tenido oportunidad de revisar la propuesta que te enviamos? Me encantaría conocer tus comentarios y resolver cualquier duda que puedas tener.\n\n¿Te parece bien si programamos una llamada rápida esta semana?\n\nSaludos,\nEquipo Cero Ideas`,
        tone: 'friendly' as const
      },
      {
        context: 'urgent_follow_up',
        response: `Hola ${contactInfo.fullName},\n\nNoto que no hemos tenido noticias tuyas en las últimas semanas. Como mencionaste que el proyecto de ${contactInfo.productInterest} era prioritario, quería asegurarme de que todo esté bien.\n\n¿Podemos programar una llamada rápida para revisar el estado del proyecto?\n\nQuedo pendiente de tu respuesta.\n\nSaludos,\nEquipo Cero Ideas`,
        tone: 'urgent' as const
      }
    ];

    const suggestion = suggestions[Math.floor(Math.random() * suggestions.length)];

    return {
      id: Date.now().toString(),
      context,
      suggestedResponse: suggestion.response,
      tone: suggestion.tone,
      confidence: Math.floor(Math.random() * 20) + 80 // 80-100%
    };
  }

  // Analyze form submissions for priority
  async analyzeFormSubmission(formData: any, contactInfo?: Contact): Promise<AIInsight> {
    await this.simulateAIProcessing(600);

    // Check if this contact was already analyzed and accepted/dismissed
    const existingInsights = this.getProcessedInsights();
    const alreadyProcessed = existingInsights.find(insight => 
      insight.contactId === contactInfo?.id && 
      insight.type === 'lead_priority' &&
      (insight.status === 'accepted' || insight.status === 'dismissed')
    );
    
    if (alreadyProcessed) {
      console.log('🚫 Lead ya fue analizado y procesado, saltando análisis:', contactInfo?.fullName);
      // Return a dummy insight that won't be shown (will be filtered out)
      return {
        id: `skip-${Date.now()}`,
        type: 'lead_priority',
        contactId: contactInfo?.id,
        title: 'Análisis omitido',
        description: 'Este lead ya fue procesado anteriormente',
        confidence: 0,
        data: { skipped: true },
        createdAt: new Date(),
        status: 'dismissed' // This will be filtered out
      };
    }

    // Try to use OpenAI if configured
    if (openaiService.isConfigured() && contactInfo) {
      try {
        console.log('🤖 Analizando prioridad con OpenAI...');
        const analysis = await openaiService.analyzeLeadPriority(contactInfo, formData);
        
        const priority = analysis.priority === 'alta' ? 'high' : 
                        analysis.priority === 'media' ? 'medium' : 'low';
        const confidence = priority === 'high' ? 95 : priority === 'medium' ? 80 : 70;
        
        return {
          id: Date.now().toString(),
          type: 'lead_priority',
          contactId: contactInfo.id,
          title: `Lead ${priority === 'high' ? 'Prioritario' : priority === 'medium' ? 'Estándar' : 'Bajo'} Detectado`,
          description: analysis.reasoning,
          confidence,
          data: { priority, analysis },
          createdAt: new Date(),
          status: 'pending'
        };
      } catch (error) {
        console.error('Error using OpenAI, falling back to mock:', error);
      }
    }

    const urgencyKeywords = ['urgente', 'inmediato', 'pronto', 'rápido', 'ya', 'ahora'];
    const highValueKeywords = ['presupuesto alto', 'gran proyecto', 'empresa grande', 'multinacional'];
    
    const message = (formData.message || '').toLowerCase();
    const hasUrgency = urgencyKeywords.some(keyword => message.includes(keyword));
    const hasHighValue = highValueKeywords.some(keyword => message.includes(keyword));

    let priority = 'medium';
    let confidence = 70;

    if (hasUrgency && hasHighValue) {
      priority = 'high';
      confidence = 95;
    } else if (hasUrgency || hasHighValue) {
      priority = 'high';
      confidence = 85;
    }

    return {
      id: Date.now().toString(),
      type: 'lead_priority',
      contactId: contactInfo?.id,
      title: `Lead ${priority === 'high' ? 'Prioritario' : 'Estándar'} Detectado`,
      description: `Análisis automático sugiere prioridad ${priority} basado en el contenido del mensaje${hasUrgency ? ' (urgencia detectada)' : ''}${hasHighValue ? ' (alto valor detectado)' : ''}.`,
      confidence,
      data: { priority, hasUrgency, hasHighValue, originalMessage: formData.message },
      createdAt: new Date(),
      status: 'pending'
    };
  }

  // Get processed insights from localStorage
  private getProcessedInsights(): AIInsight[] {
    try {
      const stored = localStorage.getItem('processed_ai_insights');
      if (stored) {
        const insights = JSON.parse(stored);
        const now = Date.now();
        const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;

        // Filter out old insights (older than 1 week) and map dates
        const recentInsights = insights
          .filter((insight: any) => {
            const createdAt = new Date(insight.createdAt).getTime();
            return (now - createdAt) < ONE_WEEK;
          })
          .map((insight: any) => ({
            ...insight,
            createdAt: new Date(insight.createdAt)
          }));

        // Save back filtered insights to clean up storage
        if (recentInsights.length < insights.length) {
          localStorage.setItem('processed_ai_insights', JSON.stringify(recentInsights));
          console.log(`🧹 Limpiados ${insights.length - recentInsights.length} insights antiguos`);
        }

        return recentInsights;
      }
    } catch (error) {
      console.error('Error loading processed insights:', error);
    }
    return [];
  }

  // Save processed insight to localStorage
  saveProcessedInsight(insight: AIInsight): void {
    try {
      const existing = this.getProcessedInsights();
      const updated = [...existing, insight];
      
      // Keep only last 100 processed insights to avoid storage bloat
      if (updated.length > 100) {
        updated.splice(0, updated.length - 100);
      }
      
      localStorage.setItem('processed_ai_insights', JSON.stringify(updated));
      console.log('💾 Insight procesado guardado:', insight.title, 'para contacto:', insight.contactId);
    } catch (error) {
      console.error('Error saving processed insight:', error);
    }
  }

  // Detect duplicate contacts
  async detectDuplicateContacts(contacts: Contact[]): Promise<DuplicateContact[]> {
    await this.simulateAIProcessing(2000);

    const duplicates: DuplicateContact[] = [];
    const processed = new Set<string>();

    for (const contact of contacts) {
      if (processed.has(contact.id)) continue;

      const potentialDuplicates = contacts.filter(c => 
        c.id !== contact.id && 
        !processed.has(c.id) &&
        (
          this.calculateSimilarity(contact.email, c.email) > 0.8 ||
          this.calculateSimilarity(contact.fullName, c.fullName) > 0.85 ||
          (contact.phone === c.phone && contact.phone.length > 5)
        )
      );

      if (potentialDuplicates.length > 0) {
        const similarity = Math.max(
          ...potentialDuplicates.map(d => 
            Math.max(
              this.calculateSimilarity(contact.email, d.email),
              this.calculateSimilarity(contact.fullName, d.fullName),
              contact.phone === d.phone ? 1 : 0
            )
          )
        );

        duplicates.push({
          primaryContact: contact,
          duplicates: potentialDuplicates,
          similarity: Math.round(similarity * 100),
          suggestedMerge: this.suggestMergedContact(contact, potentialDuplicates)
        });

        processed.add(contact.id);
        potentialDuplicates.forEach(d => processed.add(d.id));
      }
    }

    return duplicates;
  }

  // Generate lead summary (similar to contact summary but for leads)
  async generateLeadSummary(lead: Lead): Promise<ContactSummary> {
    await this.simulateAIProcessing(1500);

    // Try to use OpenAI if configured
    if (openaiService.isConfigured()) {
      try {
        console.log('🤖 Generando análisis de lead con OpenAI...');
        const aiSummary = await openaiService.generateLeadAnalysis(lead);
        
        return {
          contactId: lead.id,
          summary: aiSummary,
          keyPoints: this.extractLeadKeyPoints(lead),
          nextSteps: this.suggestLeadNextSteps(lead),
          riskLevel: this.assessLeadRiskLevel(lead),
          lastUpdated: new Date()
        };
      } catch (error) {
        console.error('Error using OpenAI for lead analysis, falling back to mock:', error);
      }
    }

    // Mock analysis
    const keyPoints = this.extractLeadKeyPoints(lead);
    const summary = this.generateLeadSummaryText(lead);
    const nextSteps = this.suggestLeadNextSteps(lead);
    const riskLevel = this.assessLeadRiskLevel(lead);

    return {
      contactId: lead.id,
      summary,
      keyPoints,
      nextSteps,
      riskLevel,
      lastUpdated: new Date()
    };
  }

  // Private helper methods
  private extractKeyPoints(contact: Contact, activities: Activity[]): string[] {
    const points = [];
    
    if (contact.leadStatus === 'en_seguimiento') {
      points.push('Cliente en proceso de seguimiento activo');
    }
    
    if (activities.length > 5) {
      points.push('Alto nivel de interacción registrada');
    }
    
    if (contact.productInterest) {
      points.push(`Interés específico en ${contact.productInterest}`);
    }
    
    const recentActivity = activities[0];
    if (recentActivity && recentActivity.datetime > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
      points.push('Actividad reciente en los últimos 7 días');
    }

    return points;
  }

  private generateSummaryText(contact: Contact, activities: Activity[]): string {
    const company = contact.company;
    const role = contact.role || 'contacto';
    const product = contact.productInterest;
    const activityCount = activities.length;

    return `${contact.fullName} es ${role} en ${company}, con interés en ${product}. Se han registrado ${activityCount} interacciones. Estado actual: ${contact.leadStatus}.`;
  }

  private suggestNextSteps(contact: Contact, activities: Activity[]): string[] {
    const steps = [];
    const lastActivity = activities[0];
    const daysSinceLastActivity = lastActivity
      ? Math.ceil((Date.now() - lastActivity.datetime.getTime()) / (1000 * 60 * 60 * 24))
      : 999;

    // Different steps based on contact status
    switch (contact.leadStatus) {
      case 'nuevo':
        steps.push('Realizar llamada de presentación inicial en 24-48 horas');
        steps.push('Enviar email de bienvenida con información corporativa');
        steps.push('Calificar el lead según criterios BANT');
        if (contact.company) {
          steps.push(`Investigar ${contact.company} en LinkedIn y web`);
        }
        break;

      case 'en_seguimiento':
        steps.push('Preparar propuesta comercial personalizada');
        steps.push('Programar reunión de descubrimiento');
        steps.push('Enviar casos de éxito relevantes');
        if (daysSinceLastActivity > 7) {
          steps.unshift('URGENTE: Retomar contacto - más de 7 días sin actividad');
        }
        break;

      case 'ya_es_cliente':
        steps.push('Realizar check-in mensual de satisfacción');
        steps.push('Identificar oportunidades de upselling');
        steps.push('Solicitar feedback y testimonios');
        steps.push('Revisar renovación de contrato si aplica');
        if (daysSinceLastActivity > 30) {
          steps.unshift('ALERTA: Cliente sin contacto hace más de 30 días');
        }
        break;

      case 'cerrado':
        steps.push('Documentar razón del cierre');
        steps.push('Programar revisión en 3-6 meses');
        steps.push('Agregar a campaña de nurturing');
        steps.push('Solicitar feedback sobre por qué no se cerró');
        break;

      case 'descartado':
        steps.push('Verificar razón del descarte');
        steps.push('Archivar para mantener base de datos limpia');
        break;

      default:
        steps.push('Establecer contacto inicial');
        steps.push('Actualizar información del contacto');
    }

    // Add general recommendations
    if (daysSinceLastActivity > 14 && contact.leadStatus !== 'descartado' && contact.leadStatus !== 'cerrado') {
      steps.push(`Seguimiento urgente - ${daysSinceLastActivity} días sin contacto`);
    }

    return steps.slice(0, 5);
  }

  // Lead-specific helper methods
  private extractLeadKeyPoints(lead: Lead): string[] {
    const points = [];
    
    if (lead.score >= 80) {
      points.push('Lead de alta prioridad con puntuación excelente');
    } else if (lead.score >= 60) {
      points.push('Lead con puntuación media-alta, buen potencial');
    } else if (lead.score < 40) {
      points.push('Lead con puntuación baja, requiere calificación');
    }
    
    if (lead.source === 'form_web') {
      points.push('Lead generado desde formulario web - interés activo');
    } else if (lead.source === 'referral') {
      points.push('Lead referido - alta probabilidad de conversión');
    }
    
    if (lead.nextFollowUp) {
      const daysUntilFollowUp = Math.ceil((lead.nextFollowUp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilFollowUp <= 0) {
        points.push('Seguimiento vencido - acción inmediata requerida');
      } else if (daysUntilFollowUp <= 3) {
        points.push(`Seguimiento programado en ${daysUntilFollowUp} días`);
      }
    } else {
      points.push('Sin seguimiento programado - considerar agendar');
    }
    
    // Parse product interest
    let productInterests = [];
    try {
      productInterests = JSON.parse(lead.productInterest);
    } catch {
      productInterests = [lead.productInterest];
    }
    
    if (productInterests.length > 1) {
      points.push(`Interés múltiple: ${productInterests.length} productos`);
    }
    
    return points;
  }

  private generateLeadSummaryText(lead: Lead): string {
    const company = lead.company;
    const role = lead.role || 'contacto';
    
    // Parse product interest
    let productText = lead.productInterest;
    try {
      const products = JSON.parse(lead.productInterest);
      if (Array.isArray(products)) {
        productText = products.length > 1 ? `${products.length} productos` : products[0];
      }
    } catch {
      // Keep original string
    }
    
    const sourceText = lead.source === 'form_web' ? 'formulario web' : 
                     lead.source === 'referral' ? 'referido' :
                     lead.source === 'phone_call' ? 'llamada telefónica' : lead.source;

    return `${lead.fullName} es ${role} en ${company}, con interés en ${productText}. Lead generado desde ${sourceText} con puntuación de ${lead.score}/100. Estado actual: ${lead.status}.`;
  }

  private suggestLeadNextSteps(lead: Lead): string[] {
    const steps = [];
    
    if (lead.status === 'nuevo') {
      steps.push('Realizar primera llamada de contacto en 24-48 horas');
      steps.push('Enviar email de bienvenida con información corporativa');
      steps.push('Calificar el lead según criterios BANT (Budget, Authority, Need, Timeline)');
    } else if (lead.status === 'contactado') {
      steps.push('Programar reunión de descubrimiento de necesidades');
      steps.push('Enviar casos de éxito relevantes al sector');
      steps.push('Preparar propuesta inicial personalizada');
    } else if (lead.status === 'calificado') {
      steps.push('Presentar propuesta comercial detallada');
      steps.push('Programar reunión de cierre');
      steps.push('Preparar documentación de contrato');
    }
    
    // Check follow-up date
    if (lead.nextFollowUp) {
      const daysUntilFollowUp = Math.ceil((lead.nextFollowUp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilFollowUp <= 0) {
        steps.unshift('URGENTE: Realizar seguimiento vencido inmediatamente');
      }
    } else {
      steps.push('Programar fecha de próximo seguimiento');
    }
    
    // Score-based suggestions
    if (lead.score >= 80) {
      steps.push('Lead prioritario - acelerar proceso comercial');
    } else if (lead.score < 40) {
      steps.push('Evaluar si el lead cumple criterios de calificación');
    }

    return steps;
  }

  private assessLeadRiskLevel(lead: Lead): 'low' | 'medium' | 'high' {
    if (lead.status === 'descartado') return 'high';
    
    // Check follow-up date
    if (lead.nextFollowUp) {
      const daysUntilFollowUp = Math.ceil((lead.nextFollowUp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilFollowUp < -7) return 'high'; // More than 7 days overdue
      if (daysUntilFollowUp < 0) return 'medium'; // Overdue but less than 7 days
    }
    
    // Check last contact
    if (lead.lastContactDate) {
      const daysSinceLastContact = Math.ceil((Date.now() - lead.lastContactDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceLastContact > 30) return 'high';
      if (daysSinceLastContact > 14) return 'medium';
    } else if (lead.status !== 'nuevo') {
      // No last contact date but not new - risky
      return 'medium';
    }
    
    // Score-based risk
    if (lead.score < 30) return 'medium';
    
    return 'low';
  }

  private assessRiskLevel(contact: Contact, activities: Activity[]): 'low' | 'medium' | 'high' {
    const lastActivity = activities[0];
    const daysSinceLastActivity = lastActivity
      ? Math.ceil((Date.now() - lastActivity.datetime.getTime()) / (1000 * 60 * 60 * 24))
      : 999;

    // Risk assessment based on contact status
    switch (contact.leadStatus) {
      case 'descartado':
        return 'high';

      case 'cerrado':
        return 'medium';

      case 'ya_es_cliente':
        // For clients, risk is about churn
        if (daysSinceLastActivity > 60) return 'high'; // 2 months without contact
        if (daysSinceLastActivity > 30) return 'medium'; // 1 month without contact
        return 'low';

      case 'en_seguimiento':
        // For leads in follow-up, risk is about losing the deal
        if (daysSinceLastActivity > 14) return 'high'; // 2 weeks without contact
        if (daysSinceLastActivity > 7) return 'medium'; // 1 week without contact
        return 'low';

      case 'nuevo':
        // For new leads, risk is about not acting fast enough
        if (daysSinceLastActivity > 7) return 'high'; // 1 week without initial contact
        if (daysSinceLastActivity > 3) return 'medium'; // 3 days without initial contact
        return 'low';

      default:
        if (daysSinceLastActivity > 30) return 'high';
        if (daysSinceLastActivity > 14) return 'medium';
        return 'low';
    }
  }

  private calculateSimilarity(str1: string, str2: string): number {
    if (!str1 || !str2) return 0;
    
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  private suggestMergedContact(primary: Contact, duplicates: Contact[]): Partial<Contact> {
    // Logic to merge contact information intelligently
    const allContacts = [primary, ...duplicates];
    
    return {
      fullName: primary.fullName, // Keep primary name
      email: primary.email, // Keep primary email
      phone: allContacts.find(c => c.phone && c.phone.length > 5)?.phone || primary.phone,
      company: primary.company,
      role: allContacts.find(c => c.role)?.role || primary.role,
      leadStatus: this.getBestLeadStatus(allContacts.map(c => c.leadStatus)),
      productInterest: primary.productInterest,
      owner: primary.owner,
      notes: allContacts.map(c => c.notes).filter(n => n).join('\n\n--- Fusión de contactos ---\n\n'),
      createdAt: new Date(Math.min(...allContacts.map(c => c.createdAt.getTime()))),
      lastActivity: new Date(Math.max(...allContacts.map(c => c.lastActivity.getTime())))
    };
  }

  private getBestLeadStatus(statuses: Contact['leadStatus'][]): Contact['leadStatus'] {
    const priority = ['cerrado', 'en_seguimiento', 'nuevo', 'descartado'];
    for (const status of priority) {
      if (statuses.includes(status)) return status;
    }
    return 'nuevo';
  }
}

export const aiService = new AIService();